import {
  AttributionOptions,
  BannerOptions,
  BlockLayouts,
  EventCardLayouts,
  ExperienceChildConfigKind,
  ExperienceLayouts,
  HeroOptions,
  LogoOptions,
  type EventCardDefinition,
  type LayoutDefinition,
} from '@/types/gql.generated';

type EventCardCapabilities = Pick<
  EventCardDefinition,
  'supportsDate' | 'supportsDescription' | 'supportsEmoji' | 'supportsLocation'
>;

const EMPTY_CARD_CAPABILITIES: EventCardCapabilities = {
  supportsDate: false,
  supportsDescription: false,
  supportsEmoji: false,
  supportsLocation: false,
};

/**
 * Returns the server-defined capability flags for the given event card layout.
 * Falls back to all-false when the layout (e.g. None) has no matching definition,
 * preventing unsupported toggles from being sent to the server. Accepts any
 * object carrying `cardOptions` so partial layout definitions (e.g. a preview
 * layout fetched with a trimmed selection set) can be passed directly.
 */
export const getEventCardCapabilities = (
  layout: Pick<LayoutDefinition, 'cardOptions'>,
  eventCardLayoutId: EventCardLayouts
): EventCardCapabilities => {
  const definition = layout.cardOptions.find(
    (o) => o.layout === eventCardLayoutId
  );
  if (!definition) return EMPTY_CARD_CAPABILITIES;
  const { supportsDate, supportsDescription, supportsEmoji, supportsLocation } =
    definition;
  return { supportsDate, supportsDescription, supportsEmoji, supportsLocation };
};

/**
 * Whether the layout exposes the event display-type controls (card/line/bullet/
 * text). Only meaningful when the server offers more than one card option;
 * single-option layouts have nothing to pick between, so the picker is hidden.
 */
export const supportsEventDisplayControls = (
  layout: Pick<LayoutDefinition, 'cardOptions'>
): boolean => {
  return layout.cardOptions.length > 1;
};

// Each takes only the option list it reads, like getEventCardCapabilities above:
// callers pass layouts from trimmed selection sets, so depending on the whole
// LayoutDefinition would break them every time the server adds a field.
export const supportsAttribution = (
  layout: Pick<LayoutDefinition, 'attributionOptions'>
): boolean => {
  return layout.attributionOptions.some(
    (item) => item !== AttributionOptions.None
  );
};

export const supportsBanner = (
  layout: Pick<LayoutDefinition, 'bannerOptions'>
): boolean => {
  return layout.bannerOptions.some((item) => item !== BannerOptions.None);
};

// Which header blocks draw the byline and the description. Not the same question
// as the layout's attributionOptions / supportsDescription: a layout offers
// several header blocks and they don't all render the same fields — nearly every
// one declares supportsDescription, the planner grids included.
const ATTRIBUTION_HEADER_BLOCKS = new Set<BlockLayouts>([
  BlockLayouts.SideHeader,
  BlockLayouts.VerticalListHeader,
  BlockLayouts.WideVerticalListHeader,
]);

const DESCRIPTION_HEADER_BLOCKS = new Set<BlockLayouts>([
  BlockLayouts.ListHeader,
  BlockLayouts.VerticalListHeader,
  BlockLayouts.WideVerticalListHeader,
]);

export const rendersHeaderAttribution = (
  blockLayoutId: BlockLayouts | null | undefined
): boolean =>
  Boolean(blockLayoutId && ATTRIBUTION_HEADER_BLOCKS.has(blockLayoutId));

export const rendersHeaderDescription = (
  blockLayoutId: BlockLayouts | null | undefined
): boolean =>
  Boolean(blockLayoutId && DESCRIPTION_HEADER_BLOCKS.has(blockLayoutId));

export const supportsHero = (
  layout: Pick<LayoutDefinition, 'heroOptions'>
): boolean => {
  return layout.heroOptions.some((item) => item !== HeroOptions.None);
};

export const supportsLogo = (
  layout: Pick<LayoutDefinition, 'logoOptions'>
): boolean => {
  return layout.logoOptions.some((item) => item !== LogoOptions.None);
};

export const isEmojiInline = (layoutId: EventCardLayouts): boolean => {
  return layoutId !== EventCardLayouts.ListEventCard;
};

// The interactive list-family layouts render events as a vertical list/table
// rather than a calendar grid. They carry a single card option (so
// supportsEventDisplayControls is false), yet still expose the color and
// Group-by controls. Centralized here so adding a new list layout is a one-line
// change instead of hunting down inline id checks.
const LIST_LAYOUTS = new Set<ExperienceLayouts>([
  ExperienceLayouts.ListTable,
  ExperienceLayouts.ListVertical,
  ExperienceLayouts.ListVerticalBulleted,
  ExperienceLayouts.ListVerticalBulletedCondensed,
  ExperienceLayouts.ListVerticalWithImages,
  // Same ListEventCard collection as ListVertical, with an image column beside it.
  ExperienceLayouts.SideHeaderV2,
  // The printable list belongs to the family too. Left out, showEventOptions
  // resolved false for it — one card option means no display-type picker, and it is
  // not a calendar — so a Printable List view offered no event options at all: no
  // emoji, time, location, category or description toggle, on a layout whose single
  // card supports every one of them.
  ExperienceLayouts.PrintableList,
]);

export const isListLayout = (layoutId: ExperienceLayouts): boolean =>
  LIST_LAYOUTS.has(layoutId);

// The month-card printables, which differ only in months-per-page (three / six /
// twelve). Grouped because every capability gate treats them alike, and because
// naming one of them individually is how the six- and twelve-box variants ended up
// missing from the calendar family below.
const MONTH_CARD_LAYOUTS = new Set<ExperienceLayouts>([
  ExperienceLayouts.PrintableYearly3BoxMonthCard,
  ExperienceLayouts.PrintableYearly6BoxMonthCard,
  ExperienceLayouts.PrintableYearlyMonthCard,
]);

// The calendar-family layouts (Month / Week grids, the legacy plannerCalendar
// Month, and the Month Cards printables). Mirrors the 'calendar' LayoutFamily the
// Add-a-view picker uses; kept here so capability gates (e.g. the text-size
// control) don't have to reach into the picker registry.
const CALENDAR_LAYOUTS = new Set<ExperienceLayouts>([
  ExperienceLayouts.PlannerMonth,
  ExperienceLayouts.PlannerWeek,
  ExperienceLayouts.PlannerCalendar,
  ExperienceLayouts.PrintableWeek,
  ...MONTH_CARD_LAYOUTS,
  ExperienceLayouts.DayByCategory,
]);

export const isCalendarLayout = (layoutId: ExperienceLayouts): boolean =>
  CALENDAR_LAYOUTS.has(layoutId);

// The canonical Planner views whose date treatment can respond to the
// datesInPrimaryColor display setting. Classic printable rendering is gated
// separately because those styleId templates are intentionally not vibe-aware.
const CONFIGURABLE_DATE_COLOR_LAYOUTS = new Set<ExperienceLayouts>([
  ...CALENDAR_LAYOUTS,
  ExperienceLayouts.ListVertical,
  ExperienceLayouts.ListVerticalBulleted,
  // Same ListEventCard collection as ListVertical, so its dates take the same
  // vibe slots and respond to the setting identically.
  ExperienceLayouts.SideHeaderV2,
  // The same list with a per-event image, so likewise.
  ExperienceLayouts.ListVerticalWithImages,
  ExperienceLayouts.PrintableList,
]);

export const rendersConfigurableDateColor = (
  layoutId: ExperienceLayouts
): boolean =>
  CONFIGURABLE_DATE_COLOR_LAYOUTS.has(layoutId) &&
  layoutId !== ExperienceLayouts.DayByCategory;

// The calendar layouts drawing a day-of-week grid of cells rather than per-box event
// lists, which is why Month Cards is excluded.
//
// plannerCalendar stays in: interactive ones are remapped to plannerMonth
// (plannerCalendarCompat) before reaching a gate, and printable ones block-render
// through ResponsiveMonth under printableBlockEnabled. Flag off they keep the classic
// template and the controls are inert there — tolerated, since excluding the id would
// also hide them from the block-rendered case.
const rendersDayGrid = (layoutId: ExperienceLayouts): boolean =>
  isCalendarLayout(layoutId) &&
  !MONTH_CARD_LAYOUTS.has(layoutId) &&
  layoutId !== ExperienceLayouts.DayByCategory;

// Where "Shade weekends" / "Mute weekend dates" have weekend cells to act on.
export const rendersWeekendCells = rendersDayGrid;

// Month Cards draws no event cards, so the Bold/Light card fill reaches it by a
// different route than every other layout: the vibes' MonthCardHeader paints the
// month title band from colorMode. Structural only — the grids gate the same
// control on their active display type, which no layout id can tell you.
export const rendersMonthCardHeader = (layoutId: ExperienceLayouts): boolean =>
  MONTH_CARD_LAYOUTS.has(layoutId);

// Where an interactive view has a day cell that can draw a today marker. The
// active vibe's display-setting capability is checked separately, so this helper
// remains purely structural and never names a vibe.
//
// Also takes the view's kind, unlike rendersWeekendCells: the control is not offered
// on a printable view, since a printed sheet is a snapshot and "today" is stale the
// day after it prints. The planner grids serve both kinds from one layout id, so the
// layout alone cannot tell them apart.
export const rendersTodayMarker = (
  layoutId: ExperienceLayouts,
  kind?: ExperienceChildConfigKind | null
): boolean =>
  rendersDayGrid(layoutId) && kind !== ExperienceChildConfigKind.Printable;

// Layouts that actually draw the category legend, so "Show color legend" has
// something to switch. Every calendar grid does, and so does the printable list —
// its ListFooter renders the same Legend, gated on the same colorLegendEnabled
// column, which left it drawing a legend the view had no control for. The
// interactive list layouts (Cards / List) are excluded: they carry
// VerticalListFooter, which has no legend.
// Where "Week starts on" has a week to start. The month-card sheets lay their
// months out as lists of dated rows rather than a day-of-week grid, so there is no
// column order for the setting to move. The server says the same thing through the
// layout's supportsWeekStart, but the sibling gates in this panel are all resolved
// here from the layout id, so this one is too.
export const rendersWeekStart = (layoutId: ExperienceLayouts): boolean =>
  !MONTH_CARD_LAYOUTS.has(layoutId) &&
  layoutId !== ExperienceLayouts.DayByCategory;

export const rendersDayColumns = (layoutId: ExperienceLayouts): boolean =>
  layoutId === ExperienceLayouts.DayByCategory;

export const rendersColorLegend = (layoutId: ExperienceLayouts): boolean =>
  isCalendarLayout(layoutId) || layoutId === ExperienceLayouts.PrintableList;

// Where an interactive list row carries the left-edge category bar. Gated by layout,
// not container: EventItemContainer also serves the ListItem card. Excluded are the
// Cards layouts (whose ListCardSurface a bar would fight) and the Printable List,
// whose row spreads the slot directly.
export const rendersListCategoryBar = (layoutId: ExperienceLayouts): boolean =>
  layoutId === ExperienceLayouts.ListVerticalBulleted;

// Where a grid event drops its timing onto a second line instead of flowing it inline
// after the title: the interactive week and month grids. Print is excluded by the
// caller: both ids also serve sheets, where the tighter inline run still applies.
// plannerCalendar needs no entry — interactive ones are remapped to plannerMonth
// before reaching this gate, and printable ones are excluded with the rest of print.
export const stacksEventTime = (layoutId: ExperienceLayouts): boolean =>
  layoutId === ExperienceLayouts.PlannerWeek ||
  layoutId === ExperienceLayouts.PlannerMonth;

// Where the Magic add-to-calendar bar is allowed above the collection. Only a planner
// child on the bulleted list opts out, since only there does the platform bar's
// Send-to-calendar already cover the job — standalone Page schedules share that layout
// with no platform bar, so the Magic bar is their only bulk export.
//
// ListVerticalBulletedCondensed is omitted on purpose: PageLayoutRenderer has no case
// for it, so it renders an ErrorState and has no bar to suppress.
export const rendersMultiExportBar = (
  layoutId: ExperienceLayouts,
  isPlannerChild: boolean
): boolean =>
  !isPlannerChild || layoutId !== ExperienceLayouts.ListVerticalBulleted;

export const isExperienceLayout = (
  value: string
): value is ExperienceLayouts => {
  const experienceLayouts: string[] = Object.values(ExperienceLayouts);

  return experienceLayouts.includes(value);
};
